import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { fetchPlanPricing, updatePlanPricing, resetPlanPricing } from '@/lib/admin/planPricing';
import { fetchAnnouncements, createAnnouncement, deactivateAnnouncement } from '@/lib/admin/announcements';
import { cn } from '@/lib/cn';

function centsToEuros(cents: number): string {
  return (cents / 100).toFixed(2);
}

function eurosToCents(value: string): number | null {
  const v = parseFloat(value);
  if (!Number.isFinite(v) || v < 0) return null;
  return Math.round(v * 100);
}

const SEVERITY_STYLES = {
  info: 'bg-sky-100 text-sky-700',
  warning: 'bg-amber-100 text-amber-700',
  critical: 'bg-rose-100 text-rose-700',
} as const;

export default function PlanPricingPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const { data: plans } = useQuery({
    queryKey: ['admin', 'plan-pricing'],
    queryFn: fetchPlanPricing,
  });
  const { data: announcements } = useQuery({
    queryKey: ['admin', 'announcements'],
    queryFn: fetchAnnouncements,
  });

  const [edits, setEdits] = useState<Record<string, { monthly: string; yearly: string }>>({});
  const [notifyUsers, setNotifyUsers] = useState<Record<string, boolean>>({});

  const updateMut = useMutation({
    mutationFn: ({ plan, monthlyPriceCents, yearlyPriceCents, notify }: { plan: string; monthlyPriceCents: number; yearlyPriceCents: number; notify: boolean }) =>
      updatePlanPricing(plan, { monthlyPriceCents, yearlyPriceCents, notifyUsers: notify }),
    onSuccess: (_, vars) => {
      toast.success(t('admin.planPricing.saved', 'Price updated'));
      if (vars.notify) {
        toast.success(t('admin.planPricing.announced', 'Users notified via dashboard alert'));
      }
      qc.invalidateQueries({ queryKey: ['admin', 'plan-pricing'] });
      qc.invalidateQueries({ queryKey: ['admin', 'announcements'] });
      qc.invalidateQueries({ queryKey: ['subscriptionPlans'] });
      qc.invalidateQueries({ queryKey: ['announcements', 'active'] });
    },
    onError: () => toast.error(t('common.error')),
  });

  const resetMut = useMutation({
    mutationFn: (plan: string) => resetPlanPricing(plan),
    onSuccess: () => {
      toast.success(t('admin.planPricing.reset', 'Reverted to default price'));
      qc.invalidateQueries({ queryKey: ['admin', 'plan-pricing'] });
      qc.invalidateQueries({ queryKey: ['subscriptionPlans'] });
    },
    onError: () => toast.error(t('common.error')),
  });

  const deactivateMut = useMutation({
    mutationFn: (id: string) => deactivateAnnouncement(id),
    onSuccess: () => {
      toast.success(t('admin.planPricing.announcementDeactivated', 'Announcement removed'));
      qc.invalidateQueries({ queryKey: ['admin', 'announcements'] });
      qc.invalidateQueries({ queryKey: ['announcements', 'active'] });
    },
    onError: () => toast.error(t('common.error')),
  });

  const items = plans ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('admin.nav.planPricing', 'Plan Pricing')}</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          {t('admin.planPricing.subtitle', 'Changing a price only affects new checkouts — existing subscribers keep their current price until they change plans.')}
        </p>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
            <tr>
              <th className="px-4 py-2">{t('admin.columns.plan')}</th>
              <th className="px-4 py-2">{t('admin.planPricing.monthly', 'Monthly (€)')}</th>
              <th className="px-4 py-2">{t('admin.planPricing.yearly', 'Yearly (€)')}</th>
              <th className="px-4 py-2">{t('admin.planPricing.notify', 'Notify users')}</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.map((p) => {
              const edit = edits[p.plan] ?? { monthly: centsToEuros(p.monthlyPriceCents), yearly: centsToEuros(p.yearlyPriceCents) };
              const notify = notifyUsers[p.plan] ?? true;
              const isFree = p.plan === 'free';
              return (
                <tr key={p.plan}>
                  <td className="px-4 py-2">
                    <div className="font-medium text-gray-900 capitalize">{p.label}</div>
                    {p.isOverridden && (
                      <span className="inline-block mt-1 rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold text-indigo-700">
                        {t('admin.planPricing.custom', 'Custom price')}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      disabled={isFree}
                      value={edit.monthly}
                      onChange={(e) => setEdits({ ...edits, [p.plan]: { ...edit, monthly: e.target.value } })}
                      className="w-24 rounded-lg border border-gray-200 px-2 py-1 text-xs disabled:bg-gray-50 disabled:text-gray-400"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      disabled={isFree}
                      value={edit.yearly}
                      onChange={(e) => setEdits({ ...edits, [p.plan]: { ...edit, yearly: e.target.value } })}
                      className="w-24 rounded-lg border border-gray-200 px-2 py-1 text-xs disabled:bg-gray-50 disabled:text-gray-400"
                    />
                  </td>
                  <td className="px-4 py-2">
                    <input
                      type="checkbox"
                      disabled={isFree}
                      checked={notify}
                      onChange={(e) => setNotifyUsers({ ...notifyUsers, [p.plan]: e.target.checked })}
                    />
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={isFree || updateMut.isPending}
                        onClick={() => {
                          const monthlyPriceCents = eurosToCents(edit.monthly);
                          const yearlyPriceCents = eurosToCents(edit.yearly);
                          if (monthlyPriceCents === null || yearlyPriceCents === null) {
                            toast.error(t('admin.planPricing.invalid', 'Invalid price'));
                            return;
                          }
                          updateMut.mutate({ plan: p.plan, monthlyPriceCents, yearlyPriceCents, notify });
                        }}
                        className="rounded-lg bg-emerald-600 px-3 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                      >
                        {t('common.save')}
                      </button>
                      {p.isOverridden && (
                        <button
                          type="button"
                          disabled={resetMut.isPending}
                          onClick={() => resetMut.mutate(p.plan)}
                          className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
                        >
                          ↺
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('admin.planPricing.announcements', 'Announcements')}</h2>
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm divide-y divide-gray-100">
          {(announcements ?? []).length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-gray-500">{t('common.noResults')}</div>
          )}
          {(announcements ?? []).map((a) => (
            <div key={a.id} className="flex items-start gap-3 px-4 py-3">
              <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase', SEVERITY_STYLES[a.severity])}>
                {a.severity}
              </span>
              <div className="flex-1 text-sm">
                <div className="font-medium text-gray-900">{a.title}</div>
                <div className="text-gray-600">{a.message}</div>
                <div className="text-xs text-gray-400 mt-0.5">{new Date(a.createdAt).toLocaleString()}</div>
              </div>
              {a.isActive && (
                <button
                  type="button"
                  disabled={deactivateMut.isPending}
                  onClick={() => deactivateMut.mutate(a.id)}
                  className="rounded-lg border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50"
                >
                  {t('admin.planPricing.deactivate', 'Remove')}
                </button>
              )}
              {!a.isActive && (
                <span className="text-xs text-gray-400">{t('admin.planPricing.inactive', 'Inactive')}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
